// Utility function to remove citation markers like [1], [2], [3], etc.
export const removeCitationMarkers = (text) => {
  if (!text || typeof text !== "string") return text;
  // Remove patterns like [1], [2], [3], [10], [99], etc. (numeric citations in square brackets)
  return text.replace(/\[\d+\]/g, "");
};

// Utility function to remove separator lines from text (like ---, ===, ___, etc.)
export const removeSeparatorLines = (text) => {
  if (!text || typeof text !== "string") return text;

  return text
    .replace(/^[-=_*~]{3,}\s*$/gm, "") // ASCII separator lines (---, ===, ___, ***, ~~~)
    .replace(/^[─━═―—\u2500-\u257F]{2,}\s*$/gmu, "") // Unicode box-drawing / typographic horizontal rules
    .replace(/^[·•‒–—]{3,}\s*$/gmu, "") // Other repeated Unicode dashes / bullets used as separators
    .replace(/\n\s*\n\s*\n/g, "\n\n") // Collapse triple+ newlines
    .trim();
};

// Utility function to ensure disclaimer is always at the end (only when response is complete)
export const ensureDisclaimerAtEnd = (text, isComplete = true) => {
  if (!text || typeof text !== "string") return text;

  // Don't add disclaimer if response is still loading/streaming
  // Only add if response is complete and has substantial content
  const trimmedText = text.trim();
  if (!isComplete || trimmedText.length < 50) {
    return text; // Too short or still loading - don't add disclaimer yet
  }

  const disclaimerText =
    "This is a preliminary legal analysis generated by AI. It is not legal advice. All conclusions should be reviewed, verified, and supplemented by a licensed attorney with full access to the case record.";

  // Check if disclaimer already exists in various formats
  const disclaimerPatterns = [
    /###\s*SECTION:\s*Disclaimer/i,
    /###\s*\*\*Disclaimer\*\*/i,
    /###\s*Disclaimer/i,
    /\*\*Disclaimer:\*\*/i,
    /<h[1-6]>\s*<strong>\s*Disclaimer\s*<\/strong>\s*<\/h[1-6]>/i,
    /Disclaimer:\s*This is a preliminary legal analysis/i,
    /This is a preliminary legal analysis generated by AI/i,
    /This is a preliminary legal synthesis generated by AI/i,
    /This is a preliminary legal analysis/i,
    /This is a preliminary legal synthesis/i,
  ];

  // Create a version of the text without HTML tags for more reliable detection
  const textForDetection = text.replace(/<[^>]*>/g, "");

  const hasDisclaimer = disclaimerPatterns.some((pattern) =>
    pattern.test(textForDetection) || pattern.test(text),
  );

  // If disclaimer doesn't exist, append it at the end
  if (!hasDisclaimer) {
    // Add appropriate format based on whether text has markdown headings
    // Check if text contains markdown section headings (### SECTION: format)
    const hasMarkdownHeadings = /#{1,6}\s+SECTION:/m.test(trimmedText);
    if (hasMarkdownHeadings) {
      return trimmedText + "\n\n### SECTION: Disclaimer\n\n" + disclaimerText;
    } else {
      return trimmedText + "\n\n**Disclaimer:**\n\n" + disclaimerText;
    }
  }

  return text;
};

// Utility to normalize heading casing and strip "SECTION:" prefixes
export const normalizeHeadingCase = (text) => {
  if (!text || typeof text !== "string") return text;

  // Strip "Section:" / "SECTION:" if it exists at the start
  let processedText = text.replace(/^(?:SECTION|Section):\s*/i, "").trim();

  const isAllCaps =
    processedText.length >= 3 &&
    processedText === processedText.toUpperCase() &&
    /[A-Z]/.test(processedText) &&
    processedText !== processedText.toLowerCase(); // Has uppercase letters

  if (isAllCaps) {
    return processedText
      .split(/\s+/)
      .map((word) => {
        if (word.length === 0) return word;
        const match = word.match(/^([^a-zA-Z]*)([a-zA-Z]+)([^a-zA-Z]*)$/);
        if (match) {
          const prefix = match[1] || "";
          const letters = match[2];
          const suffix = match[3] || "";

          // Special case: Keep "AI" in uppercase
          if (letters.toUpperCase() === "AI") {
            return prefix + "AI" + suffix;
          }

          // Special case: Keep Roman numerals in uppercase (e.g., "VII.")
          if (/^[ivxlcdm]+$/i.test(letters) && /^(?:m{0,4}(?:cm|cd|d?c{0,3})(?:xc|xl|l?x{0,3})(?:\bix|\biv|v?i{0,3}))$/i.test(letters)) {
            return prefix + letters.toUpperCase() + suffix;
          }

          return (
            prefix +
            letters.charAt(0).toUpperCase() +
            letters.slice(1).toLowerCase() +
            suffix
          );
        }
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
      })
      .join(" ");
  }

  return processedText;
};

// Format text to HTML with proper headings, sections, and structure (same as Dashboard)
export const convertTextToHtml = (value = "") => {
  if (value === null || value === undefined) return "";
  // Ensure value is a string to prevent runtime errors
  if (typeof value !== "string") {
    try {
      value = String(value);
    } catch (e) {
      console.error("Error converting value to string:", e);
      return "";
    }
  }
  const isHtml = /<\/?[a-z][\s\S]*>/i.test(value);
  if (isHtml) {
    // If it's already HTML, fix formatting
    let html = value;

    // CRITICAL FIX: Fix malformed numbered items where markdown bold was removed incorrectly
    // Pattern like "**1. Priority**" becomes "1. Priority" but sometimes becomes "1Priority"
    // Fix: "1Priority of Title:" -> "1. Priority of Title:"
    // Also handles leading whitespace and ensures space after period
    html = html.replace(/^(\s*)(\d+)([A-Z])/gm, "$1$2. $3");
    // Fix cases where there's already a period but no space: "1.Priority" -> "1. Priority"
    html = html.replace(/^(\s*)(\d+)\.([A-Z])/gm, "$1$2. $3");

    // CRITICAL FIX: Convert markdown headings to HTML even when HTML is detected
    // This handles cases where responses have mixed HTML and markdown (common with Gemini)
    html = html.replace(
      /^(#{1,6})\s+(?:SECTION:\s*)?(.+)$/gm,
      (match, hashes, text) => {
        const level = Math.min(hashes.length, 6);
        const tag = `h${level}`;
        // Strip "SECTION:" if it's still in the text
        let headingText = text.trim().replace(/^SECTION:\s*/i, "");
        const normalizedText = normalizeHeadingCase(headingText);
        return `<${tag}><strong>${normalizedText}</strong></${tag}>`;
      },
    );

    // Convert Roman numeral headings (e.g., "I. Issue(s) Presented", "II. Relevant Rules")
    // This handles Gemini's tendency to follow the IRAC structure literally
    // Use uppercase Romans consistently (I., II., III., IV., V.)
    // Handle optionally bolded lines (**ii. Title**)
    html = html.replace(/^(?:\*\*|<strong>)?([IVXLCDM]+)\.\s+(.+?)(?:\*\*|<\/strong>)?$/gim, (match, roman, text) => {
      if (roman.length <= 10) {
        const romanUpper = roman.toUpperCase();
        return `<h2><strong>${romanUpper}. ${text.trim()}</strong></h2>`;
      }
      return match;
    });

    // Dynamic heading detection (no longer hardcoded)
    html = html.replace(
      /^(?:#{1,6})\s*(?:SECTION:\s*)?(.+)$/gim,
      (match, text) => {
        const normalizedText = normalizeHeadingCase(text.trim());
        return `<h2><strong>${normalizedText}</strong></h2>`;
      },
    );

    // Convert markdown bold "**Disclaimer:**" format to proper heading
    html = html.replace(
      /\*\*Disclaimer:\*\*\s*\n?\s*/gi,
      "<h2><strong>Disclaimer</strong></h2>\n",
    );

    const blockSplitter = /(?=<p>|<br\s*\/?>|\n)/;
    const htmlBlocks = html.split(blockSplitter);
    html = htmlBlocks
      .map((block) => {
        const headingMatch = block.match(
          /^(?:<p>|<br\s*\/?>|\n)?\s*(?:\*\*|<strong>)(?:SECTION:\s*)?(.+?)(?::|(?:\*\*|<\/strong>))\s*(?:<\/p>|(?::|\s|$))/i,
        );
        if (headingMatch && headingMatch.index < 15) {
          const sectionName = headingMatch[1].trim();
          const normalizedText = normalizeHeadingCase(sectionName);
          const remaining = block
            .substring(block.indexOf(headingMatch[0]) + headingMatch[0].length)
            .trim();

          if (
            !remaining ||
            remaining.startsWith("<br") ||
            remaining.startsWith("\n") ||
            remaining.startsWith("</p>")
          ) {
            return `<h2><strong>${normalizedText}</strong></h2>${remaining}`;
          }
          return `<h2><strong>${normalizedText}</strong></h2><p>${remaining}</p>`;
        }
        return block;
      })
      .join("");

    // Preserve <ol> tags for numbered lists - don't convert to <ul>
    // Only process list items to clean up formatting, but preserve numbers in <ol> lists
    html = html.replace(
      /<li>([^<]*(?:<[^>]+>[^<]*<\/[^>]+>[^<]*)*)<\/li>/gi,
      (match, content) => {
        // Check if this list item is inside an <ol> (ordered list)
        // We'll preserve the number if it's in an <ol>, otherwise strip it for <ul>
        const listContext = match; // We'll check parent context differently

        const numberMatch = content.match(/^(\s*)(\d+)([\.\)])\s+(.+)$/);
        if (numberMatch) {
          // Check if parent is <ol> - if so, keep the number stripped (browser will number it)
          // If parent is <ul>, also strip it (it's a bullet list)
          return `<li>${numberMatch[1]}${numberMatch[4]}</li>`;
        }

        const numberMatchWithTags = content.match(
          /^(\s*)(<[^>]+>)?(\s*)(\d+)([\.\)])\s+(.+?)(<\/[^>]+>)?$/,
        );
        if (numberMatchWithTags && numberMatchWithTags[4]) {
          const prefix = numberMatchWithTags[1] || "";
          const openingTag = numberMatchWithTags[2] || "";
          const closingTag = numberMatchWithTags[7] || "";
          const text = numberMatchWithTags[6] || "";
          return `<li>${prefix}${openingTag}${text}${closingTag}</li>`;
        }

        const bulletMatch = content.match(/^(\s*)([-•*▪▫◦‣⁃])\s+(.+)$/);
        if (bulletMatch) {
          return `<li>${bulletMatch[1]}${bulletMatch[3]}</li>`;
        }

        return match;
      },
    );

    html = html.replace(
      /<h1>([^<]*(?:<[^>]+>[^<]*<\/[^>]+>[^<]*)*)<\/h1>/gi,
      (match, content) => {
        let text = content.replace(/<\/?strong>/g, "").trim();
        return `<h1><strong>${text}</strong></h1>`;
      },
    );
    html = html.replace(
      /<h2>([^<]*(?:<[^>]+>[^<]*<\/[^>]+>[^<]*)*)<\/h2>/gi,
      (match, content) => {
        let text = content.replace(/<\/?strong>/g, "").trim();
        return `<h2><strong>${text}</strong></h2>`;
      },
    );
    html = html.replace(
      /<h3>([^<]*(?:<[^>]+>[^<]*<\/[^>]+>[^<]*)*)<\/h3>/gi,
      (match, content) => {
        let text = content.replace(/<\/?strong>/g, "").trim();
        return `<h3><strong>${text}</strong></h3>`;
      },
    );

    return html;
  }

  let cleanedValue = value.replace(/\*/g, "");
  cleanedValue = cleanedValue.replace(/^(\d+)([A-Z])/gm, "$1. $2");

  const lines = cleanedValue.split("\n");
  const formattedLines = [];
  let inList = false;
  let listItems = [];
  let isNumberedList = false;

  const dropTrailingBreak = () => {
    while (formattedLines.length && formattedLines[formattedLines.length - 1] === "<br/>") {
      formattedLines.pop();
    }
  };

  const EXEC_SUMMARY_HEADINGS = new Set([
    "EXECUTIVE SUMMARY",
    "CASE POSTURE",
    "TOP 3 STRENGTHS",
    "TOP 3 VULNERABILITIES",
    "CRITICAL DECISION POINT",
    "IMMEDIATE NEXT STEPS",
  ]);

  const normalizeExecutiveSummaryHeading = (line) => {
    const raw = (line || "").toString().trim();
    if (!raw) return "";
    const noMarkdown = raw.replace(/[*_`]/g, "").trim();
    const noColon = noMarkdown.replace(/:\s*$/, "").trim();
    return noColon.toUpperCase();
  };

  const closeList = () => {
    if (listItems.length > 0) {
      const listTag = isNumberedList ? "ol" : "ul";
      formattedLines.push(`<${listTag}>${listItems.join("")}</${listTag}>`);
      listItems = [];
    }
    inList = false;
  };

  for (let index = 0; index < lines.length; index++) {
    const line = lines[index];
    const trimmedLine = line.trim();

    if (!trimmedLine) {
      // Keep ordered lists contiguous across visual spacer lines when the next
      // non-empty line is also numbered. Otherwise each item becomes its own
      // one-item <ol>, which renders every item as "1.".
      if (inList && isNumberedList) {
        let nextNonEmptyLine = "";
        for (let lookahead = index + 1; lookahead < lines.length; lookahead++) {
          const candidate = (lines[lookahead] || "").trim();
          if (candidate) {
            nextNonEmptyLine = candidate;
            break;
          }
        }
        if (/^\d+[\.\)]\s+.+/.test(nextNonEmptyLine)) {
          continue;
        }
      }

      closeList();
      // Avoid stacking multiple blank lines into huge vertical gaps
      if (formattedLines[formattedLines.length - 1] !== "<br/>") {
        formattedLines.push("<br/>");
      }
      continue;
    }

    // Executive Summary: force known section titles into headings (even without trailing colon,
    // and even when content appears on the same line like "CASE POSTURE: STRONG – ...").
    // This keeps the structure readable while streaming.
    const colonIdx = trimmedLine.indexOf(":");
    const leftSide = (colonIdx >= 0 ? trimmedLine.slice(0, colonIdx) : trimmedLine)
      .trim();
    const rightSide = (colonIdx >= 0 ? trimmedLine.slice(colonIdx + 1) : "")
      .trim();

    const maybeExecHeading = normalizeExecutiveSummaryHeading(leftSide);
    if (EXEC_SUMMARY_HEADINGS.has(maybeExecHeading)) {
      closeList();
      dropTrailingBreak();
      const headingText =
        maybeExecHeading === "CASE POSTURE"
          ? "CASE POSTURE:"
          : maybeExecHeading;
      formattedLines.push(`<h2><strong>${headingText}</strong></h2>`);
      if (rightSide) {
        formattedLines.push(`<p>${rightSide}</p>`);
      }
      continue;
    }

    // Handle "**Disclaimer:**" format specifically
    const disclaimerBoldMatch = trimmedLine.match(/^\*\*Disclaimer:\*\*\s*$/i);
    if (disclaimerBoldMatch) {
      closeList();
      dropTrailingBreak();
      formattedLines.push(`<h2><strong>Disclaimer</strong></h2>`);
      // The disclaimer text should be on the next line
      continue;
    }

    const dynamicHeadingMatch = trimmedLine.match(
      /^(?:(?:#{1,6})\s*(?:SECTION:\s*)?|(?:(?:\*\*|SECTION:)\s*)(?:SECTION:\s*)?)(.+?)(?::|\*\*|$)/i,
    );

    if (dynamicHeadingMatch) {
      closeList();
      dropTrailingBreak();
      const sectionName = dynamicHeadingMatch[1].trim();
      const remainingContent = trimmedLine
        .substring(dynamicHeadingMatch[0].length)
        .trim();
      const normalizedText = normalizeHeadingCase(sectionName);

      formattedLines.push(`<h2><strong>${normalizedText}</strong></h2>`);
      if (remainingContent) {
        formattedLines.push(`<p>${remainingContent}</p>`);
      }
      continue;
    }

    const headingMatch = line.match(/^(#{1,6})\s+(?:SECTION:\s*)?(.+)$/);
    if (headingMatch) {
      closeList();
      dropTrailingBreak();
      const level = headingMatch[1].length;
      // Strip "SECTION:" if it's still in the text
      let headingText = headingMatch[2].trim().replace(/^SECTION:\s*/i, "");
      headingText = normalizeHeadingCase(headingText);
      const tag = `h${Math.min(level, 6)}`;
      formattedLines.push(`<${tag}><strong>${headingText}</strong></${tag}>`);
      continue;
    }

    const romanMatch = trimmedLine.match(/^([IVXLCDM]+)\.\s+(.+)$/i);
    if (romanMatch && romanMatch[1].length <= 10) {
      closeList();
      dropTrailingBreak();
      const romanNumeral = romanMatch[1].toUpperCase();
      const headingText = romanMatch[2].trim();
      formattedLines.push(
        `<h2><strong>${romanNumeral}. ${headingText}</strong></h2>`,
      );
      continue;
    }

    const bulletMatch = trimmedLine.match(/^([-•*▪▫◦‣⁃])\s+(.+)$/);
    const numberMatch = trimmedLine.match(/^(\d+[\.\)])\s+(.+)$/);

    if (bulletMatch) {
      if (inList && isNumberedList) closeList();
      listItems.push(`<li>${bulletMatch[2]}</li>`);
      inList = true;
      isNumberedList = false;
      continue;
    } else if (numberMatch) {
      if (inList && !isNumberedList) closeList();
      listItems.push(`<li>${numberMatch[2]}</li>`);
      inList = true;
      isNumberedList = true;
      continue;
    }

    // Check if line looks like a heading (section header ending with colon)
    // Increased character limit to 120 to accommodate longer section titles
    if (
      trimmedLine.length > 0 &&
      trimmedLine.length < 120 &&
      trimmedLine.endsWith(":")
    ) {
      closeList();
      dropTrailingBreak();
      const nextLine = index < lines.length - 1 ? lines[index + 1].trim() : "";
      const prevLine = index > 0 ? lines[index - 1].trim() : "";

      // Determine heading level based on context
      let headingLevel = "h3"; // Default to sub-section
      if (prevLine === "" && nextLine !== "" && !/^[-•\d]/.test(nextLine)) {
        headingLevel = "h2"; // Section heading (preceded by blank, followed by content)
      } else if (prevLine === "" && nextLine === "") {
        headingLevel = "h2"; // Section divider
      } else if (prevLine !== "" && nextLine === "") {
        headingLevel = "h3"; // Sub-section (end of section)
      }

      // Remove colon for heading text
      const headingText = trimmedLine.slice(0, -1);
      formattedLines.push(
        `<${headingLevel}><strong>${headingText}</strong></${headingLevel}>`,
      );
      continue;
    }

    // Regular paragraph line - ADD INLINE STYLES TO CONTROL MARGINS
    closeList();
    formattedLines.push(
      `<p style="margin-top: 0.35rem; margin-bottom: 0.35rem;">${line}</p>`,
    );
  }

  // Close any remaining list
  closeList();

  // Join formatted lines
  return formattedLines.join("");
};
